import {
  Lead,
  GuideSection,
  P,
  UL,
  LI,
  Term,
  GuideLink,
  Callout,
  GuideTable,
} from './guide-ui';

/** Body for "What is SOAR?" guide. */
export function WhatIsSoarGuideBody() {
  return (
    <>
      <Lead>
        SOAR, Security Orchestration, Automation and Response, is how security
        teams act on what their SIEM detects, faster and more consistently than by
        hand. It connects tools, automates repetitive response steps into playbooks,
        and manages cases end to end. The hard part is not the automation; it is
        deciding what an agent or playbook may do on its own. This guide explains
        SOAR and where the human stays in control.
      </Lead>

      <GuideSection id="what-is-soar" title="What is SOAR?">
        <P>
          <Term>Security Orchestration, Automation and Response (SOAR)</Term> is a
          category of tooling that helps security operations respond to incidents by
          coordinating across systems, automating routine actions, and managing the
          response workflow. Where a SIEM <Term>detects</Term>, SOAR{' '}
          <Term>acts</Term>.
        </P>
      </GuideSection>

      <GuideSection id="three-pillars" title="The three pillars">
        <UL>
          <LI><Term>Orchestration</Term>: connect the tools in your stack (firewall, identity, EDR, ticketing) so they can work together in one flow.</LI>
          <LI><Term>Automation</Term>: turn repetitive response steps into automated sequences, so analysts do not do them by hand every time.</LI>
          <LI><Term>Response</Term>: manage the incident end to end: case management, evidence, notes, and escalation.</LI>
        </UL>
      </GuideSection>

      <GuideSection id="playbooks" title="What is a playbook?">
        <P>
          A <Term>playbook</Term> is a defined sequence of response steps that runs
          when a trigger fires, for example: enrich the alert with threat intel,
          open a ticket, notify the on-call analyst, and (optionally) contain the
          threat by blocking an IP or disabling an account. Playbooks make response
          fast and consistent, and they document exactly what happened.
        </P>
      </GuideSection>

      <GuideSection id="soar-vs-siem" title="SOAR vs. SIEM">
        <GuideTable
          head={['', 'SIEM', 'SOAR']}
          rows={[
            ['Primary job', 'Detect: collect, correlate, alert', 'Respond: orchestrate, automate, manage'],
            ['Input', 'Logs and security telemetry', 'Alerts and incidents (often from the SIEM)'],
            ['Output', 'Prioritised detections / notables', 'Executed response and managed cases'],
          ]}
        />
        <P>
          The line has blurred: modern SIEMs increasingly embed SOAR-style response
          rather than requiring a separate product. See{' '}
          <GuideLink href="/guides/what-is-siem">what is SIEM</GuideLink>.
        </P>
      </GuideSection>

      <GuideSection id="governance" title="The governance question: how much to automate">
        <P>
          The benefit of SOAR is speed; the risk is an automated action you did not
          intend. Notifying a human or opening a ticket is safe to fully automate.{' '}
          <Term>Destructive</Term> actions, disabling a user, blocking traffic,
          isolating a host, can disrupt the business if triggered on a false
          positive.
        </P>
        <Callout title="Automate enrichment, gate the consequences">
          <p>
            A sound default: automate the low-risk, reversible steps (enrich,
            ticket, notify) and put a <Term>human approval gate</Term> in front of
            anything destructive. This is doubly important for regulated teams and
            when AI agents drive the response.
          </p>
        </Callout>
      </GuideSection>

      <GuideSection id="how-logpulse-helps" title="How LogPulse approaches response">
        <P>
          LogPulse response actions, notify, create a ticket, block an IP, disable a
          user, run behind a <Term>human approval gate</Term> and can be composed
          into playbooks. AI agents (over the{' '}
          <GuideLink href="/guides/mcp-for-security">MCP server</GuideLink>) can draft
          response playbooks, but they are created inactive until a human approves,
          and destructive actions need owner approval. That is the difference between
          an{' '}
          <GuideLink href="/guides/what-is-an-agentic-soc">agentic SOC</GuideLink>{' '}
          with a human in the loop and unattended automation. See{' '}
          <GuideLink href="/agentic-soc">Agentic SOC</GuideLink> for how it fits.
        </P>
      </GuideSection>
    </>
  );
}
